/**
 * 🎨 KPIStatCard - Key Performance Indicator Card
 * 
 * Für die Darstellung von KPIs im Affiliate Dashboard:
 * - Große Werte (Earnings, Conversions, etc.)
 * - Icon mit Farb-Variante
 * - Trend-Indikator
 * - Hover-Effekt
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, type LucideIcon, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ============================================
// TYPES
// ============================================

export type IconVariant = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
export type TrendDirection = 'up' | 'down' | 'neutral';

export interface KPITrend {
  /** Trend-Wert in Prozent (z.B. 12.5 für +12.5%) */
  value: number;
  /** Trend-Richtung */
  direction: TrendDirection;
  /** Vergleichszeitraum (z.B. "vs. letzter Monat") */
  period?: string;
}

export interface KPIStatCardProps {
  /** Angezeigter Wert (z.B. "1.234,56 €" oder "42") */
  value: string;
  /** Beschreibendes Label */
  label: string;
  /** Trend gegenüber Vorperiode */
  trend?: KPITrend;
  /** Icon für die Karte */
  icon: LucideIcon;
  /** Icon-Hintergrund Farbe */
  iconVariant?: IconVariant;
  /** Optionaler Tooltip */
  tooltip?: string;
  /** Loading State */
  isLoading?: boolean;
  /** Highlighted State (für wichtigste KPI) */
  isHighlighted?: boolean;
  /** Click Handler für Detail-View */
  onClick?: () => void;
  /** Zusätzliche CSS Klassen */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const ICON_VARIANT_STYLES: Record<IconVariant, { bg: string; text: string; gradient: string }> = {
  primary: {
    bg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    text: 'text-white',
    gradient: 'from-emerald-500/15 to-emerald-500/5',
  },
  success: {
    bg: 'bg-gradient-to-br from-green-500 to-green-600',
    text: 'text-white',
    gradient: 'from-green-500/12 to-green-500/3',
  },
  warning: {
    bg: 'bg-gradient-to-br from-amber-500 to-amber-600',
    text: 'text-white',
    gradient: 'from-amber-500/12 to-amber-500/3',
  },
  error: {
    bg: 'bg-gradient-to-br from-red-500 to-red-600',
    text: 'text-white',
    gradient: 'from-red-500/12 to-red-500/3',
  },
  info: {
    bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
    text: 'text-white',
    gradient: 'from-blue-500/12 to-blue-500/3',
  },
  neutral: {
    bg: 'bg-gradient-to-br from-gray-500 to-gray-600',
    text: 'text-white',
    gradient: 'from-gray-500/12 to-gray-500/3',
  },
};

const TREND_STYLES: Record<TrendDirection, { icon: LucideIcon; color: string }> = {
  up: {
    icon: TrendingUp,
    color: 'text-green-600',
  },
  down: {
    icon: TrendingDown,
    color: 'text-red-600',
  },
  neutral: {
    icon: Minus,
    color: 'text-gray-500',
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface TrendIndicatorProps {
  trend: KPITrend;
  invertColors?: boolean;
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({ trend, invertColors = false }) => {
  const config = TREND_STYLES[trend.direction];
  const TrendIcon = config.icon;
  
  // Bei invertColors ist "down" grün (z.B. bei Kosten)
  let colorClass = config.color;
  if (invertColors) {
    if (trend.direction === 'up') colorClass = 'text-red-600';
    if (trend.direction === 'down') colorClass = 'text-green-600';
  }

  return (
    <div className={cn('flex items-center gap-1 text-xs', colorClass)}>
      <TrendIcon className="w-3.5 h-3.5" />
      <span className="font-medium">
        {trend.direction !== 'neutral' && (trend.direction === 'up' ? '+' : '-')}
        {Math.abs(trend.value).toFixed(1)}%
      </span>
      {trend.period && (
        <span className="text-gray-500 ml-1">{trend.period}</span>
      )}
    </div>
  );
};

// ============================================
// LOADING SKELETON
// ============================================

const KPIStatCardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn(
    'bg-white rounded-xl p-6',
    'shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)]',
    className
  )}>
    <div className="flex items-start gap-4">
      <Skeleton className="w-14 h-14 rounded-xl" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-24 mt-2" />
      </div>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export const KPIStatCard: React.FC<KPIStatCardProps> = ({
  value,
  label,
  trend,
  icon: Icon,
  iconVariant = 'primary',
  tooltip,
  isLoading = false,
  isHighlighted = false,
  onClick,
  className,
}) => {
  if (isLoading) {
    return <KPIStatCardSkeleton className={className} />;
  }

  const iconStyles = ICON_VARIANT_STYLES[iconVariant];

  const cardContent = (
    <div
      className={cn(
        // Glassmorphism Base
        'relative overflow-hidden',
        'bg-white/80 backdrop-blur-xl',
        'rounded-2xl p-4 sm:p-5 md:p-6',
        'border border-white/60',
        'shadow-[0_4px_24px_rgba(0,0,0,0.06)]',
        // Gradient Background based on variant
        `bg-gradient-to-br ${iconStyles.gradient}`,
        // Transitions
        'transition-colors duration-300 ease-out',
        // Hover Effects
        onClick && 'cursor-pointer',
        'hover:shadow-[0_8px_32px_rgba(0,0,0,0.1)] ',
        // Highlighted State
        isHighlighted && [
          'ring-2 ring-emerald-500/30',
          'shadow-[0_8px_32px_rgba(16,185,129,0.2)]',
        ],
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Icon Container */}
        <div className={cn(
          'w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0',
          'shadow-lg',
          iconStyles.bg
        )}>
          <Icon className={cn('w-5 h-5 sm:w-7 sm:h-7', iconStyles.text)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Value */}
          <div className="flex items-baseline flex-wrap gap-x-1.5 sm:gap-x-2">
            <span className="text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-gray-900 tracking-tight break-words">
              {value}
            </span>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                    <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-sm">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Label */}
          <div className="text-[11px] xs:text-xs sm:text-sm text-gray-500 mt-0.5 leading-tight">
            {label}
          </div>

          {/* Trend */}
          {trend && (
            <div className="mt-2">
              <TrendIndicator trend={trend} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      {cardContent}
    </TooltipProvider>
  );
};

// ============================================
// KPI STAT CARD GRID
// ============================================

interface KPIStatCardGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

export const KPIStatCardGrid: React.FC<KPIStatCardGridProps> = ({
  children,
  columns = 4,
  className,
}) => {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={cn(
      'grid gap-4 md:gap-6',
      gridCols[columns],
      className
    )}>
      {children}
    </div>
  );
};

export default KPIStatCard;
